import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { TorchButton } from "./torch-button";
import { ContourButton } from "./contour-button";
import { ContourOverlay } from "./contour-overlay";
import { ScanIndication } from "./scan-indication";
import { CaptureFlash } from "./capture-flash";
import { ReferenceContour } from "./reference-contour";
import { CameraSwitchButton } from "./camera-switch-button";
import { BackButton } from "./back-button";
import { ScanTimerButton } from "./scan-timer-button";
import { CaptureButton } from "./capture-button";
import { ScanStatusOverlay } from "./scan-status-overlay";
import {
  SCAN_FOR_OTHER,
  SCAN_USER_SET,
  SCAN_FOR_SELF,
} from "../prescan-slide/prescan-configure-screen";
import { PrescanSlideView } from "../prescan-slide/prescan-slide-view";
import { TutorialVideoView } from "./tutorial-video-view";
import { PortraitLock } from "../portrait-lock";
import { useCamera } from "./use-camera";
import { useDeviceTilt } from "./use-device-tilt";
import { useScanCapture, MIN_CAPTURES } from "./use-scan-capture";
import { useScanJob } from "./use-scan-job";
import { ModelViewer } from "../viewer/model-viewer";
import {
  isTutorialHidden,
  setTutorialHidden,
  loadBodyPartSettings,
  saveBodyPartSetting,
} from "../../services/scan-storage";

// Body types that support tutorial slide instead of just video
const SLIDE_BODIES = ["shoe", "both shoes", "fo"];
// Scan POVs determined from body part -- null if set by user
const SCAN_POV = {
  shoe: SCAN_USER_SET,
  "both shoes": SCAN_USER_SET,
  fo: SCAN_FOR_SELF,
  "lower body": SCAN_FOR_OTHER,
};
// Body types that require tilt detection
const TILT_GUIDE_VALUES = { shoe: -125, "both shoes": -130 };
// body parts scanned with the front camera until the user picks the other one.
const DEFAULT_FRONT_CAMERA = ["fo"];
// start timer (seconds) a scan starts with;
// body parts not listed start with none.
const DEFAULT_START_DELAY = { fo: 3 };
// auto stop image count a scan starts with (until the user picks another);
// body parts not listed start with auto stop off.
const DEFAULT_AUTO_STOP = { fo: 45, shoe: 60, "both shoes": 60 };

// Fullscreen live rear-camera scan view.
//
// onSaved (optional, used in multiple-scan mode): when given, the viewer's save
// button hands the finished scan's info back to the caller (the dashboard)
// instead of uploading the order data directly.
// onBack (optional, used in multiple-scan mode): shows a back button at the top
// left that abandons the scan and returns to the caller.
export const ScanView = ({ order, fileFormat, onSaved, onBack }) => {
  // Scan target
  const { bodyPart, bodyPartName, sided, useMetrics } = order.order.bodyGroup;
  const name = bodyPartName || bodyPart;
  const side = order.order.requiredScan.side;
  const title = sided ? `${side} ${name}` : name;
  const hasSlide = SLIDE_BODIES.includes(bodyPart.toLowerCase());
  const hasTilt = Object.hasOwn(TILT_GUIDE_VALUES, bodyPart.toLowerCase());
  const tiltGuideValue = TILT_GUIDE_VALUES[bodyPart.toLowerCase()] || 0;

  // Prescan configuration: scan POV & paper type (if applicable)
  const initialScanPov = SCAN_POV[bodyPart.toLowerCase()];
  const [scanPov, setScanPov] = useState(initialScanPov);
  const [paperType, setPaperType] = useState(null);

  // Actual target tilt value based on scan POV (self vs. someone else)
  const getTargetTilt = () => {
    if (!hasTilt) return 0;
    if (scanPov === null) return tiltGuideValue;
    switch (scanPov) {
      case SCAN_FOR_SELF:
        return tiltGuideValue;
      case SCAN_FOR_OTHER:
        return tiltGuideValue + 90;
      default:
        return tiltGuideValue;
    }
  };
  const targetTilt = getTargetTilt();

  // the automatic measurement of the finished scan, when the user ran it —
  // { jobId, dataUrl, modelUrl, modelUrlAt, complete, sides }. Saved
  // with the scan so reviewing it later can show the measured mesh again.
  const [measurement, setMeasurement] = useState(null);

  // scanning contour overlay. note not every body part has one right now, so we
  // hide it (and its button) if the SVG fails to load.
  const [contourAvailable, setContourAvailable] = useState(true);
  const hasContour = contourAvailable;

  // optional tutorial video/slide shown before scanning.
  const tutorialVideo = order.order.bodyGroup.tutorialVideo;
  const hasTutorial =
    hasSlide ||
    (Boolean(tutorialVideo) && tutorialVideo.toLowerCase() !== "na");

  // whether the scan includes a reference object (QR code, credit card, etc.).
  const hasReference = Boolean(
    order.visionUseQRCode || order.visionUseCreditCard || order.visionUsePaper,
  );

  // where to send the user once their scan is saved (optional).
  const returnUrl = order.order.returnUrl;

  // show the tutorial video first (when present, unless the user chose to hide
  // it for this body part on a previous scan).
  // UPDATE: Now defined before `useCamera` so the function can read
  //         the value of `showTutorial`
  const [showTutorial, setShowTutorial] = useState(
    () => hasTutorial && !isTutorialHidden(bodyPart),
  );

  // Permission state set by the tutorial slide.
  const [cameraPermitted, setCameraPermitted] = useState(false);

  // camera stream, torch, front/rear switching, and frame capture.
  const {
    attachVideo,
    videoKey,
    error,
    preparing,
    switchCamera: switchCameraRaw,
    facingMode,
    torchOn,
    torchSupported,
    toggleTorch,
    captureFrame,
  } = useCamera({
    bodyPart,
    // open the camera as soon as permission is granted (even mid-tutorial) so
    // the preview is ready by the time the user reaches the scanner; the torch
    // stays off until the tutorial is dismissed.
    active: !showTutorial || cameraPermitted,
    torchAllowed: !showTutorial,
    defaultFront: DEFAULT_FRONT_CAMERA.includes(bodyPart.toLowerCase()),
  });

  // Device tilt detection
  const { tilt, supported, motionPermission, requestMotionPermission } =
    useDeviceTilt({
      active: hasTilt && !showTutorial,
    });
  const [tiltReady, setTiltReady] = useState(false);
  const tiltGateOn = hasTilt && supported && motionPermission === "granted";
  const tiltOk = !tiltGateOn || tiltReady;

  // the server side of the scan: upload, processing progress, and save.
  const {
    uploadState,
    uploadError,
    progress,
    modelUrl,
    modelUrlAt,
    jobData,
    saved,
    uploadScan,
    retryUpload,
    resetJob,
    saveData,
  } = useScanJob({ order, fileFormat, title, paperType });

  // save the scan: in multiple-scan mode hand the scan info back to the
  // dashboard (uploaded later, all scans together); otherwise upload now.
  // thumbnail: small snapshot of the viewed model, shown in the dashboard grid.
  async function handleSave(thumbnail) {
    if (onSaved) {
      onSaved({
        scanType: bodyPart,
        side,
        title,
        measurement,
        jobId: jobData && jobData.jobId,
        dataUrl: jobData && jobData.dataUrl,
        modelName: jobData && jobData.modelNames && jobData.modelNames[0],
        modelUrl,
        modelUrlAt,
        thumbnail,
      });
      return;
    }
    await saveData({ measurement });
  }

  // the capture run: frame grabbing, start timer, and auto stop.
  const {
    recording,
    captureCount,
    countdown,
    canStop,
    startDelay,
    setStartDelay,
    autoStop,
    setAutoStop,
    toggleCapture,
    abortCapture,
    resetCapture,
  } = useScanCapture({
    bodyPart,
    captureFrame,
    defaultStartDelay: DEFAULT_START_DELAY[bodyPart.toLowerCase()] || 0,
    defaultAutoStop: DEFAULT_AUTO_STOP[bodyPart.toLowerCase()] || 0,
    onFinish: (frames) => {
      // scanning is done — no need to keep the torch on during upload.
      if (torchOn) toggleTorch(false);
      uploadScan(frames);
    },
  });

  // dismiss the tutorial, optionally remembering not to show it again for this
  // body part.
  function dismissTutorial(hide) {
    if (hide) setTutorialHidden(bodyPart);
    setShowTutorial(false);
  }

  // overlay/control state — the user's last contour mode is restored.
  const [contourMode, setContourMode] = useState(
    () => loadBodyPartSettings(bodyPart).contourMode || "upright",
  );

  // Local prescan configuration handler
  // -- sets contour direction based on scan POV settings (self vs. someone else)
  const localOnScanPovSet = (pov) => {
    switch (pov) {
      case SCAN_FOR_SELF:
        setContourMode("upright");
        break;
      case SCAN_FOR_OTHER:
        setContourMode("flipped");
        break;
      default:
        setContourMode("upright");
    }
    setScanPov(pov);
  };

  // once data saved, send the user to the order's return URL (if one is set).
  useEffect(() => {
    if (saved && returnUrl) {
      window.location.href = returnUrl;
    }
  }, [saved, returnUrl]);

  // the torch is turned off when a capture finishes; turn it back to the
  // user's preference (on by default) whenever the scanner returns to a fresh
  // capture — e.g. rescanning after a failure, or discarding the model in the
  // viewer.
  // NOTE: The torch must not be on when tutorial is being displayed.
  useEffect(() => {
    if (!showTutorial && !uploadState && !recording && torchSupported) {
      toggleTorch(loadBodyPartSettings(bodyPart).torchOn !== false);
    }
  }, [
    showTutorial,
    uploadState,
    recording,
    torchSupported,
    toggleTorch,
    bodyPart,
  ]);

  // the user flipped the torch — remember the choice for the next scan.
  function setTorch(on) {
    saveBodyPartSetting(bodyPart, "torchOn", on);
    toggleTorch(on);
  }

  // discard the current scan and return to a fresh capture (camera is still
  // live under the overlay).
  function resetScan() {
    resetCapture();
    resetJob();
    setMeasurement(null);
  }

  function cycleContour() {
    const next =
      contourMode === "off"
        ? "upright"
        : contourMode === "upright"
          ? "flipped"
          : "off";
    saveBodyPartSetting(bodyPart, "contourMode", next);
    setContourMode(next);
  }

  // toggle between the rear and front camera (only allowed before scanning).
  function switchCamera() {
    if (recording) return;
    switchCameraRaw();
  }

  // Back vs. abort button states
  const backButtonActive = onBack && !recording && !uploadState;
  const abortButtonActive = recording && !uploadState;

  // Controlling whether capture button is interactable
  const canStart = !preparing && tiltOk;
  const buttonActive = recording ? canStop : canStart;
  const buttonDisabled = !recording && countdown === 0 && !canStart;

  // Tilt indicator state
  const showTiltIndicator = tiltGateOn && !recording && countdown === 0;

  // the model data has been saved and uploaded. If the order defined a return
  // URL, the effect above redirects there (show a brief note meanwhile);
  // otherwise show a terminal success message.
  if (saved) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1400,
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 4,
          textAlign: "center",
        }}
      >
        {returnUrl ? (
          <>
            <CircularProgress />
            <Typography color="text.secondary">Redirecting…</Typography>
          </>
        ) : (
          <>
            <Typography variant="h5">Scan uploaded</Typography>
            <Typography color="text.secondary">
              Your scan has been uploaded successfully. You can close this page
              now.
            </Typography>
          </>
        )}
      </Box>
    );
  }

  // once processing succeeds with a result model, show the viewer instead of
  // the scanner (this unmounts the camera). Deleting returns to a fresh scan.
  if (uploadState === "succeeded" && modelUrl) {
    return (
      <>
        <PortraitLock />
        <ModelViewer
          url={modelUrl}
          title={title}
          scanType={bodyPart}
          useMetrics={useMetrics}
          onMeasured={setMeasurement}
          metricsInput={{
            relatedLabId: order.order.labId,
            webOrderId: order.order.webOrderId,
            webOrderUserName: order.order.webOrderUserName,
            dataUrl: jobData && jobData.dataUrl,
            dataFilename:
              jobData && jobData.modelNames && jobData.modelNames[0],
          }}
          onDelete={resetScan}
          onSave={handleSave}
          saveConfirm={!onSaved}
          thumbnail={Boolean(onSaved)}
        />
      </>
    );
  }

  // show the tutorial video before the scanner (the camera warms up in the
  // background meanwhile, so the preview is ready once the user continues).
  if (showTutorial) {
    if (hasSlide) {
      return (
        <PrescanSlideView
          bodyPart={bodyPart.toLowerCase()}
          onBack={onBack}
          onContinue={dismissTutorial}
          onPermissionGranted={() => setCameraPermitted(true)}
          motionPermission={motionPermission}
          onRequestMotion={hasTilt ? requestMotionPermission : undefined}
          initialScanPov={initialScanPov}
          mayUsePaper={Boolean(order.visionUsePaper)}
          onScanPovSet={localOnScanPovSet}
          onPaperTypeSet={setPaperType}
        />
      );
    } else {
      return (
        <TutorialVideoView
          url={tutorialVideo}
          title={`${name} Scan`}
          onPermissionGranted={() => setCameraPermitted(true)}
          onContinue={dismissTutorial}
        />
      );
    }
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: "#000",
        overflow: "hidden",
        zIndex: 1300,
      }}
    >
      {/* fullscreen/portrait-lock prompt, shown once the camera view is up */}
      <PortraitLock />

      {/* The scanner video */}
      <Box
        component="video"
        key={videoKey}
        ref={attachVideo}
        autoPlay
        playsInline
        muted
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          // un-flip the front camera's preview
          transform: facingMode === "user" ? "scaleX(-1)" : "none",
        }}
      />

      {/* the camera can take a few seconds to get ready on some phones */}
      {preparing && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#000",
            color: "#fff",
            px: 4,
            textAlign: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6">Getting the camera ready…</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            This may take a few seconds. Please hold on.
          </Typography>
        </Box>
      )}

      {/* body-part contour overlay */}
      {hasContour && !preparing && !recording && !uploadState && (
        <ContourOverlay
          bodyPart={bodyPart}
          side={side}
          mode={contourMode}
          mirrored={facingMode === "user"}
          hasReference={hasReference}
          onUnavailable={() => setContourAvailable(false)}
        />
      )}

      {/* reference object contour (QR code / credit card) — it accompanies
          the body contour, so it only shows while that exists and is on */}
      {!preparing &&
        !uploadState &&
        !recording &&
        hasContour &&
        contourMode !== "off" && (
          <ReferenceContour
            bodyPart={bodyPart}
            useQRCode={Boolean(order.visionUseQRCode)}
            useCreditCard={Boolean(order.visionUseCreditCard)}
            usePaper={Boolean(order.visionUsePaper)}
            paperType={paperType}
            mode={contourMode}
            side={side}
            mirrored={facingMode === "user"}
          />
        )}

      {/* a white flash over the preview on every captured image */}
      {recording && <CaptureFlash count={captureCount} />}

      {/* 
        center dot +
        reference-object hint (QR code / credit card) +
        tilt indicator
      */}
      {!preparing && !uploadState && (
        <ScanIndication
          recording={recording}
          useQRCode={Boolean(order.visionUseQRCode)}
          useCreditCard={Boolean(order.visionUseCreditCard)}
          usePaper={Boolean(order.visionUsePaper)}
          showTilt={showTiltIndicator}
          tilt={tilt}
          targetTilt={targetTilt}
          onTiltReady={setTiltReady}
        />
      )}

      {/* title at the top center */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          // keep clear of the notch/front camera when fullscreen
          pt: "max(24px, env(safe-area-inset-top))",
          px: 2,
        }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{
            backgroundColor: "#fff",
            borderRadius: 10,
            px: 2,
            py: 1,
            boxShadow: 2,
            textTransform: "capitalize",
          }}
        >
          {title}
        </Typography>
      </Box>

      {/*
        Back button at the top left to discard any data collected
        from the scanning session.
        - Always enabled if dashboard is available.
        - Enabled once scanning is started if dashboard is not there.
      */}
      <Box
        sx={{
          position: "absolute",
          top: "max(24px, env(safe-area-inset-top))",
          left: 16,
        }}
      >
        {backButtonActive && <BackButton icon="back" onClick={onBack} />}
        {abortButtonActive && (
          <BackButton icon="close" onClick={abortCapture} />
        )}
      </Box>

      {/* camera switch and scan timers at the top right — only before a scan
          starts */}
      {!recording && !uploadState && (
        <Box
          sx={{
            position: "absolute",
            top: "max(24px, env(safe-area-inset-top))",
            right: 16,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CameraSwitchButton onClick={switchCamera} />
          <ScanTimerButton
            startDelay={startDelay}
            onStartDelay={setStartDelay}
            autoStop={autoStop}
            onAutoStop={setAutoStop}
            autoStopCount={MIN_CAPTURES}
          />
        </Box>
      )}

      {/* start-timer countdown over the center of the preview */}
      {countdown > 0 && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontSize: 120,
              fontWeight: "bold",
              textShadow: "0 0 16px rgba(0,0,0,0.6)",
            }}
          >
            {countdown}
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 16,
            right: 16,
            transform: "translateY(-50%)",
          }}
        >
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* round scan button at the bottom center */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          pb: 5,
        }}
      >
        <CaptureButton
          recording={recording}
          active={buttonActive}
          count={captureCount}
          countingDown={countdown > 0}
          disabled={buttonDisabled}
          onClick={toggleCapture}
        />
      </Box>

      {/* scan option buttons at the bottom right */}
      <Box
        sx={{
          position: "absolute",
          right: 16,
          bottom: 56, // vertically centered on the scan button row
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {hasContour && (
          <ContourButton
            mode={contourMode}
            enabled={!recording}
            onClick={cycleContour}
          />
        )}
        {torchSupported ? (
          <TorchButton on={torchOn} enabled={!recording} onToggle={setTorch} />
        ) : (
          <Box sx={{ width: 40, height: 40 }} />
        )}
      </Box>

      {/* "powered by" logo at the bottom left */}
      <Box
        sx={{
          position: "absolute",
          left: 16,
          bottom: 40, // matches the button container's pb: 5 (5 * 8px)
          height: 72, // matches the button height so centers line up
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/power-by.png`}
          alt="Powered by"
          sx={{
            height: 36,
            maxWidth: "40vw",
            objectFit: "contain",
          }}
        />
      </Box>

      {/* process status overlay — covers the controls once the scan finished */}
      <ScanStatusOverlay
        state={uploadState}
        error={uploadError}
        progress={progress}
        captureCount={captureCount}
        onRetry={retryUpload}
        onRescan={resetScan}
      />
    </Box>
  );
};

ScanView.propTypes = {
  order: PropTypes.object.isRequired,
  fileFormat: PropTypes.string,
  onSaved: PropTypes.func,
  onBack: PropTypes.func,
};
